import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, GeoJSON, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../contexts/AuthContext';
import { occurrenceService, categoryService } from '../services/occurrence.service';
import { cityService } from '../services/auth.service';
import { Category } from '../types';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

function ChangeMapView({ center }: { center: [number, number] }) {
  const map = useMap();
  map.setView(center, map.getZoom());
  return null;
}

function LocationMarker({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function ReportPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState<string>('');
  const [lat, setLat] = useState<number>(-26.9185);
  const [lng, setLng] = useState<number>(-49.0658);
  const [address, setAddress] = useState<string>('');
  const [boundary, setBoundary] = useState<any>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const activeCityId = user?.activeCityId || user?.primaryCityId || user?.cityId;

  useEffect(() => {
    if (activeCityId) {
      cityService.getBoundary(activeCityId).then(setBoundary).catch(() => {});
      cityService.getById(activeCityId).then((city) => {
        if (city?.lat && city?.lng) {
          setLat(Number(city.lat));
          setLng(Number(city.lng));
        }
      }).catch(() => {});
      categoryService.findByCity(activeCityId).then((cats) => {
        setCategories(cats);
        if (cats.length && !categoryId) setCategoryId(cats[0].id);
      }).catch(() => {});
    }
  }, [activeCityId]);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    selected.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPreviews((p) => [...p, reader.result as string]);
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=pt`,
        { headers: { 'User-Agent': 'GeoZ/1.0 (admin@geoz.com)' } },
      );
      const data = await res.json();
      const a = data?.address;
      if (a) {
        const parts: string[] = [];
        const street = a.road || a.pedestrian || a.footway || a.street || a.path || '';
        if (street) parts.push(a.house_number ? `${street}, ${a.house_number}` : street);
        const neighbourhood = a.suburb || a.neighbourhood || a.quarter || a.village || a.town || '';
        if (neighbourhood) parts.push(neighbourhood);
        setAddress(parts.join(', '));
      } else {
        setAddress('');
      }
    } catch {
      setAddress('');
    }
  };

  const onLocationChange = (lat: number, lng: number) => {
    setLat(lat);
    setLng(lng);
    reverseGeocode(lat, lng);
  };

  const detectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLat(pos.coords.latitude);
          setLng(pos.coords.longitude);
          reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        },
        () => {
          if (!lat || !lng) {
            setLat(-26.9185);
            setLng(-49.0658);
          }
        },
        { enableHighAccuracy: false, timeout: 10000 },
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description) {
      alert('Preencha a descrição');
      return;
    }
    setSubmitting(true);
    try {
      let images: string[] = [];
      if (files.length) {
        const uploaded = await occurrenceService.uploadFiles(files);
        images = uploaded.map((u) => u.url);
      }
      await occurrenceService.create({ description, categoryId, lat, lng, address: address || undefined, images });
      navigate('/');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message;
      alert(msg || 'Erro ao registrar ocorrência');
    } finally {
      setSubmitting(false);
    }
  };

  return (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">Localização</label>
              <button type="button" onClick={detectLocation} className="text-sm text-primary-600 hover:text-primary-700">
                Detectar localização
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {lat.toFixed(6)}, {lng.toFixed(6)} — Clique no mapa para ajustar
            </p>
            <div className="h-64 rounded-lg overflow-hidden">
              <MapContainer center={[lat, lng]} zoom={15} className="h-full w-full">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {boundary && (
                  <GeoJSON
                    key={activeCityId}
                    data={boundary}
                    style={{
                      color: '#3b82f6',
                      weight: 2,
                      opacity: 0.6,
                      fillColor: '#3b82f6',
                      fillOpacity: 0.08,
                    }}
                  />
                )}
                <Marker position={[lat, lng]} />
                <ChangeMapView center={[lat, lng]} key={`${lat}-${lng}`} />
                <LocationMarker onLocationChange={onLocationChange} />
              </MapContainer>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Preenchido automaticamente ao clicar no mapa"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Fotos</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {previews.map((src, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 text-sm text-gray-700">
              <input
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handleFiles}
                className="hidden"
              />
              📷 Adicionar foto
            </label>
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-lg">
            {submitting ? 'Registrando...' : 'Registrar Ocorrência'}
          </button>
        </form>
  );
}